import { auditLogger, CSRFProtection } from './security';
import { getSupabase } from './supabase';

export const sessionManager = {
  isSessionValid: async (): Promise<boolean> => {
    try {
      const supabase = await getSupabase();
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session) {
        auditLogger.log('SESSION_EXPIRED', { reason: error ? 'session_error' : 'no_session' });
        await sessionManager.clearSession();
        return false;
      }

      if (session.expires_at) {
        const expiryTime = session.expires_at * 1000;
        const now = Date.now();

        if (now >= expiryTime) {
          try {
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              auditLogger.log('SESSION_EXPIRED', { reason: 'jwt_expired_refresh_failed' });
              await sessionManager.clearSession();
              return false;
            }
          } catch {
            auditLogger.log('SESSION_EXPIRED', { reason: 'jwt_expired_refresh_exception' });
            await sessionManager.clearSession();
            return false;
          }
        } else {
          const bufferTime = 5 * 60 * 1000;
          if (now >= expiryTime - bufferTime) {
            try {
              await supabase.auth.refreshSession();
            } catch {}
          }
        }
      }

      const sessionStart = localStorage.getItem('session_start');
      const startTime = sessionStart ? parseInt(sessionStart, 10) : NaN;
      const now = Date.now();
      const sessionTimeout = 2 * 60 * 60 * 1000;
      const needsBootstrap = !sessionStart || isNaN(startTime) || now - startTime > sessionTimeout;

      if (needsBootstrap) {
        auditLogger.log('SESSION_BOOTSTRAPPED', { reason: 'rebuilt_from_valid_supabase_session' });
        localStorage.setItem('session_start', now.toString());
      }

      return true;
    } catch {
      await sessionManager.clearSession();
      return false;
    }
  },

  getSession: async () => {
    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        return { user: session.user, isSupabase: true };
      }
      return null;
    } catch {
      return null;
    }
  },

  clearSession: async () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('authToken');
    localStorage.removeItem('session_start');
    CSRFProtection.clearToken();
    sessionStorage.clear();
    try {
      const supabase = await getSupabase();
      await supabase.auth.signOut();
    } catch {}
  },

  refreshSession: () => {
    localStorage.setItem('session_start', Date.now().toString());
  },

  getCurrentUser: async () => {
    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        return {
          id: session.user.id,
          email: session.user.email,
          username: session.user.user_metadata?.username || session.user.email,
          isSupabase: true,
        };
      }
      return null;
    } catch {
      return null;
    }
  },

  getSessionExpiryTime: async (): Promise<number | null> => {
    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.expires_at) {
        return session.expires_at * 1000;
      }
      return null;
    } catch {
      return null;
    }
  },
};
